import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom"; // <-- add useNavigate
import AdminSidebar from "../shared/AdminSidebar";
import { getNodeApiBase } from "../../../utils/apiBase";

const GridEvaluation = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(today);

  // Criteria state (already present)
  const [criteria, setCriteria] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCriterionTitle, setNewCriterionTitle] = useState("");
  // BEFORE: const [newSubCriteria, setNewSubCriteria] = useState([""]);
  // AFTER: keep note per sous-critère as object { title, note }
  const [newSubCriteria, setNewSubCriteria] = useState([{ title: "", note: "" }]);

  // NEW: preview mode
  const [showPreview, setShowPreview] = useState(false);

  // NEW: which critère is being edited (null when adding)
  const [editingId, setEditingId] = useState(null);

  const navigate = useNavigate(); // <-- navigation hook
  const { id } = useParams(); // edit mode if present
  const nodeApiBase = getNodeApiBase();

  // Responsive viewport width for centering next to sidebar on large screens
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isNarrow = viewportWidth <= 992; // breakpoint to drop sidebar offset

  // Load evaluation for edit: try backend by ID, fallback to localStorage
  useEffect(() => {
    if (!id) return;
    const apiBase = nodeApiBase;

    const load = async () => {
      try {
  const resp = await fetch(`${apiBase}/api/evaluation/list`);
        const json = await resp.json();
        const list = Array.isArray(json?.data) ? json.data : [];
        const found = list.find((g) => String(g.ID_Evaluation) === String(id));
        if (found) {
          setTitle(found.Titre_evaluation || "");
          setCriteria(
            Array.isArray(found.criteria)
              ? found.criteria.map((c) => ({
                  id: c.ID_criteres,
                  title: c.Titre_criteres,
                  // Map to objects including note from backend (fallbacks remain supported)
                  subCriteria: Array.isArray(c.subCriteria)
                    ? c.subCriteria.map((s) => ({
                        title: s?.Titre_sous_critres || String(s?.title || s || ""),
                        note:
                          s?.note !== undefined && s?.note !== null && s?.note !== ""
                            ? String(s.note)
                            : "",
                      }))
                    : [],
                }))
              : []
          );
          setDate((d) => d || new Date().toISOString().slice(0, 10));
          return;
        }
      } catch (e) {
        console.error("Failed to load evaluation from backend", e);
      }
      // Fallback: localStorage
      try {
        const all = JSON.parse(localStorage.getItem("grids") || "[]");
        const localFound = all.find((g) => String(g.id) === String(id));
        if (localFound) {
          setTitle(localFound.title || "");
          setDate(localFound.date || new Date().toISOString().slice(0, 10));
          // Ensure subCriteria items are objects with { title, note }
          setCriteria(
            Array.isArray(localFound.criteria)
              ? localFound.criteria.map((c) => ({
                  ...c,
                  subCriteria: Array.isArray(c.subCriteria)
                    ? c.subCriteria.map((s) =>
                        typeof s === "string" ? { title: s, note: "" } : { title: s.title || "", note: s.note || "" }
                      )
                    : [],
                }))
              : []
          );
        }
      } catch {}
    };

    load();
  }, [id, nodeApiBase]);

  // Centralized save + redirect
  // Replace the existing saveGridAndGo with an async version that posts to backend first
  const saveGridAndGo = async () => {
    const apiBase = nodeApiBase;

    const payload = { title, criteria };

    try {
  const url = id ? `${apiBase}/api/evaluation/${id}` : `${apiBase}/api/evaluation`;
      const method = id ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        console.error('Failed to save evaluation to backend', await resp.text());
      }
    } catch (e) {
      console.error('Error calling backend evaluation endpoint:', e);
    }

    // Keep existing local save so your list continues to work
    const all = JSON.parse(localStorage.getItem("grids") || "[]");
    if (id) {
      const next = all.map((g) =>
        String(g.id) === String(id) ? { ...g, title, date, criteria } : g
      );
      localStorage.setItem("grids", JSON.stringify(next));
    } else {
      all.push({ id: Date.now(), title, date, criteria });
      localStorage.setItem("grids", JSON.stringify(all));
    }

    navigate("/admin-evaluation-grid-list");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveGridAndGo();
  };

  // NEW: handlers for criteria form
  const handleAddCriterionClick = () => {
    setShowAddForm(true);
    setNewCriterionTitle("");
    // BEFORE: setNewSubCriteria([""]);
    setNewSubCriteria([{ title: "", note: "" }]);
  };
  const handleAddSousCritere = () =>
    // BEFORE: setNewSubCriteria([...newSubCriteria, ""]);
    setNewSubCriteria([...newSubCriteria, { title: "", note: "" }]);
  const handleChangeSousCritere = (idx, key, val) => {
    const next = [...newSubCriteria];
    next[idx] = { ...next[idx], [key]: val };
    setNewSubCriteria(next);
  };
  const handleSaveCriterion = (e) => {
    e.preventDefault();
    if (!newCriterionTitle.trim()) return;
    // Clean and keep only sous-critères with a title
    const cleanedSubs = newSubCriteria
      .map((s) => ({
        title: String(s.title || "").trim(),
        note: String(s.note ?? "").trim(),
      }))
      .filter((s) => s.title.length > 0);

    if (editingId) {
      setCriteria((prev) =>
        prev.map((c) =>
          c.id === editingId
            ? {
                ...c,
                title: newCriterionTitle.trim(),
                subCriteria: cleanedSubs,
              }
            : c
        )
      );
    } else {
      setCriteria((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: newCriterionTitle.trim(),
          subCriteria: cleanedSubs,
        },
      ]);
    }

    setShowAddForm(false);
    setNewCriterionTitle("");
    // BEFORE: setNewSubCriteria([""]);
    setNewSubCriteria([{ title: "", note: "" }]);
    setEditingId(null);
  };

  // NEW: start editing a critère
  const handleEditCriterion = (id) => {
    const found = criteria.find((c) => c.id === id);
    if (!found) return;
    setNewCriterionTitle(found.title);
    // Normalize to objects
    setNewSubCriteria(
      Array.isArray(found.subCriteria) && found.subCriteria.length
        ? found.subCriteria.map((s) =>
            typeof s === "string" ? { title: s, note: "" } : { title: s.title || "", note: s.note || "" }
          )
        : [{ title: "", note: "" }]
    );
    setEditingId(id);
    setShowAddForm(true);
  };

  // NEW: delete a critère
  const handleDeleteCriterion = (id) => {
    setCriteria((prev) => prev.filter((c) => c.id !== id));
    // If deleting the one currently being edited, reset the form
    if (editingId === id) {
      setShowAddForm(false);
      setNewCriterionTitle("");
      setNewSubCriteria([""]);
      setEditingId(null);
    }
  };

  // Center the card while keeping the sidebar intact
  const layoutStyle = {
    // Keep space for the admin sidebar on wide screens; full width on small
    marginLeft: isNarrow ? 0 : "250px",
    width: isNarrow ? "100vw" : "calc(100vw - 250px)",
    boxSizing: "border-box",
    minHeight: "100vh",
    display: "flex",
    alignItems: "flex-start", // align to top of the screen
    justifyContent: "center",
    background: "#e4d2d2",
    padding: "24px 16px", // comfortable top padding
  };

  const cardStyle = {
    width: "100%",
    // Make the card bigger while staying elegant and centered
    maxWidth: isNarrow ? "95vw" : "1100px",
    background: "#fff",
    border: "1px solid #e6e8eb",
    borderRadius: "12px",
    boxShadow: "0 8px 16px rgba(16,24,40,0.08)",
    overflow: "hidden",
  };

  const headerStyle = {
    background: "#0b7a3e",
    color: "#fff",
    textAlign: "center",
    padding: "16px 20px",
    fontWeight: 700,
    fontSize: "1.25rem",
    letterSpacing: ".3px",
  };

  const bodyStyle = { padding: "22px" };
  const groupStyle = { marginBottom: "16px" };
  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontWeight: 600,
    color: "#344054",
  };
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #d0d5dd",
    background: "#fff",
    fontSize: "14px",
    color: "#111827",
  };
  const buttonStyle = {
    width: "100%",
    padding: "12px",
    background: "#0b7a3e",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: 700,
    cursor: "pointer",
  };
  // NEW: small/secondary buttons and container styles
  const smallButtonStyle = {
    padding: "8px 12px",
    background: "#0b7a3e",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  };
  const secondaryButtonStyle = {
    padding: "8px 12px",
    background: "#edf2f7",
    color: "#111827",
    border: "1px solid #d0d5dd",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  };
  const cancelButtonStyle = {
    padding: "10px 12px",
    background: "#f3f4f6",
    color: "#111827",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  };
  const formContainerStyle = {
    padding: "16px",
    border: "1px solid #e6e8eb",
    borderRadius: "10px",
    background: "#fbfbfc",
    marginBottom: "16px",
  };
  // Optional: small danger style for delete button
  const dangerSmallButtonStyle = {
    padding: "8px 12px",
    background: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  };

  // Pill buttons for Actions column
  const editPillBtn = {
    padding: "8px 14px",
    borderRadius: "12px",
    background: "#198754",
    color: "#fff",
    border: "none",
    fontWeight: 600,
    fontSize: "0.9rem",
    lineHeight: 1,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  };
  const deletePillBtn = {
    ...editPillBtn,
    background: "#dc3545",
  };

  // Render helper to show sub-critères as "Title (Note: n)"
  const renderSubCriteriaInline = (arr) => {
    if (!Array.isArray(arr) || !arr.length) return "-";
    return arr
      .map((s) => {
        const t = typeof s === "string" ? s : s?.title || "";
        const n = typeof s === "object" ? s?.note : "";
        return t + (n ? ` (Note: ${n})` : "");
      })
      .join(", ");
  };

  return (
    <>
      <AdminSidebar />
      <div style={layoutStyle}>
        <div style={cardStyle}>
          <div style={headerStyle}>Créer une grille d’évaluation</div>
          <div style={bodyStyle}>
            {!showPreview ? (
              <>
                {/* Top form (title/date) */}
                <form onSubmit={handleSubmit}>
                  <div style={groupStyle}>
                    <label style={labelStyle}>Titre Grille d’évaluation</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Entrer le titre"
                      style={inputStyle}
                      required
                    />
                  </div>
                  {/* <div style={groupStyle}>
                    <label style={labelStyle}>Date</label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div> */}
                </form>

                {/* Criteria section */}
                <div style={{ marginTop: 24 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: "1.1rem" }}>Critères</h3>
                    <button
                      type="button"
                      style={smallButtonStyle}
                      onClick={handleAddCriterionClick}
                    >
                      ajouter une critere
                    </button>
                  </div>

                  {showAddForm && (
                    <form
                      onSubmit={handleSaveCriterion}
                      style={formContainerStyle}
                    >
                      <div style={groupStyle}>
                        <label style={labelStyle}>Titre de Critère</label>
                        <input
                          type="text"
                          value={newCriterionTitle}
                          onChange={(e) => setNewCriterionTitle(e.target.value)}
                          placeholder="Titre du critere"
                          style={inputStyle}
                          required
                        />
                      </div>
                      <div
                        style={{
                          marginBottom: 8,
                          fontWeight: 600,
                          color: "#344054",
                        }}
                      >
                        Titre Sous critères
                      </div>
                      {newSubCriteria.map((sc, idx) => (
                        <div key={idx} style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 120px", alignItems: "center", marginBottom: 10 }}>
                          <input
                            type="text"
                            value={sc.title}
                            onChange={(e) => handleChangeSousCritere(idx, "title", e.target.value)}
                            placeholder={`Sous critere ${idx + 1}`}
                            style={inputStyle}
                          />
                          <input
                            type="number"
                            min="0"
                            max="20"
                            step="0.5"
                            value={sc.note}
                            onChange={(e) => handleChangeSousCritere(idx, "note", e.target.value)}
                            placeholder="Note"
                            style={{ ...inputStyle, padding: "10px 8px" }}
                            title="Note (paramètre) pour ce sous critère"
                          />
                        </div>
                      ))}
                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          flexWrap: "wrap",
                          marginTop: 8,
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <button
                          type="button"
                          style={secondaryButtonStyle}
                          onClick={handleAddSousCritere}
                        >
                          ajouter un sous critere
                        </button>

                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            marginLeft: "auto",
                            marginTop: "35px",
                          }}
                        >
                          <button type="submit" style={smallButtonStyle}>
                            {editingId ? "Mettre à jour" : "enregitrer"}
                          </button>
                          <button
                            type="button"
                            style={cancelButtonStyle}
                            onClick={() => {
                              setShowAddForm(false);
                              setNewCriterionTitle("");
                              setNewSubCriteria([""]);
                              setEditingId(null);
                            }}
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Criteria table (live view before saving) */}
                  <div
                    style={{
                      marginTop: 12,
                      border: "1px solid #e6e8eb",
                      borderRadius: "10px",
                      overflow: "hidden",
                      background: "#fff",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        tableLayout: "auto", // allow content to define width to keep buttons on one line
                      }}
                    >
                      <thead>
                        <tr style={{ background: "#f3f4f6" }}>
                          <th
                            style={{
                              padding: 12,
                              textAlign: "left",
                              borderBottom: "1px solid #e6e8eb",
                            }}
                          >
                            Critère
                          </th>
                          <th
                            style={{
                              padding: 12,
                              textAlign: "left",
                              borderBottom: "1px solid #e6e8eb",
                            }}
                          >
                            Sous-critères
                          </th>
                          {/* NEW: Actions column with wider width */}
                          <th
                            style={{
                              padding: 12,
                              textAlign: "left",
                              borderBottom: "1px solid #e6e8eb",
                              whiteSpace: "nowrap",
                              width: "220px",
                            }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {criteria.length === 0 ? (
                          <tr>
                            <td
                              colSpan={3}
                              style={{
                                padding: 12,
                                textAlign: "center",
                                color: "#6b7280",
                              }}
                            >
                              Aucun critère
                            </td>
                          </tr>
                        ) : (
                          criteria.map((c) => (
                            <tr key={c.id}>
                              <td
                                style={{
                                  padding: 12,
                                  borderTop: "1px solid #f1f3f5",
                                  verticalAlign: "top",
                                  wordBreak: "break-word",
                                }}
                              >
                                {c.title}
                              </td>
                              <td
                                style={{
                                  padding: 12,
                                  borderTop: "1px solid #f1f3f5",
                                  verticalAlign: "top",
                                  wordBreak: "break-word",
                                }}
                              >
                                {Array.isArray(c.subCriteria) && c.subCriteria.length
                                  ? renderSubCriteriaInline(c.subCriteria)
                                  : "-"}
                              </td>
                              {/* Actions keep on single line */}
                              <td
                                style={{
                                  padding: 15,
                                  borderTop: "1px solid #f1f3f5",
                                  verticalAlign: "middle",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <div
                                  style={{
                                    display: "inline-flex",
                                    gap: 8,
                                    flexWrap: "nowrap",
                                    alignItems: "center",
                                  }}
                                >
                                  <button
                                    type="button"
                                    style={editPillBtn}
                                    onClick={() => handleEditCriterion(c.id)}
                                  >
                                    modifier
                                  </button>
                                  <button
                                    type="button"
                                    style={deletePillBtn}
                                    onClick={() => handleDeleteCriterion(c.id)}
                                  >
                                    supprimer
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              // PREVIEW MODE: display saved grid with criteria
              <div>
                <div
                  style={{
                    marginBottom: 16,
                    padding: "14px 16px",
                    border: "1px solid #e6e8eb",
                    borderRadius: 10,
                    background: "#fbfbfc",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "1.2rem",
                      marginBottom: 6,
                    }}
                  >
                    Grille d’évaluation
                  </div>
                  <div style={{ color: "#374151" }}>
                    <div>
                      <strong>Titre:</strong> {title}
                    </div>
                    <div>
                      <strong>Date:</strong> {date}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid #e6e8eb",
                    borderRadius: "10px",
                    overflow: "hidden",
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f3f4f6" }}>
                        <th
                          style={{
                            padding: 12,
                            textAlign: "left",
                            borderBottom: "1px solid #e6e8eb",
                          }}
                        >
                          Critère
                        </th>
                        <th
                          style={{
                            padding: 12,
                            textAlign: "left",
                            borderBottom: "1px solid #e6e8eb",
                          }}
                        >
                          Sous-critères
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {criteria.length === 0 ? (
                        <tr>
                          <td
                            colSpan={2}
                            style={{
                              padding: 12,
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            Aucun critère défini
                          </td>
                        </tr>
                      ) : (
                        criteria.map((c) => (
                          <tr key={c.id}>
                            <td
                              style={{
                                padding: 12,
                                borderTop: "1px solid #f1f3f5",
                                verticalAlign: "top",
                                wordBreak: "break-word",
                              }}
                            >
                              {c.title}
                            </td>
                            <td
                              style={{
                                padding: 12,
                                borderTop: "1px solid #f1f3f5",
                                verticalAlign: "top",
                                wordBreak: "break-word",
                              }}
                            >
                              {Array.isArray(c.subCriteria) && c.subCriteria.length
                                ? renderSubCriteriaInline(c.subCriteria)
                                : "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Optional: back to edit */}
                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowPreview(false)}
                    style={{
                      padding: "10px 12px",
                      background: "#f3f4f6",
                      color: "#111827",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Modifier
                  </button>
                </div>
              </div>
            )}
            <br />
            <button type="button" style={buttonStyle} onClick={saveGridAndGo}>
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GridEvaluation;
